<section aria-labelledby="disconnect-heading">
  <h2 id="disconnect-heading">Create Disconnect Ticket</h2>

  @if (disconnectTxGroup) {
    <form
      [formGroup]="disconnectTxGroup"
      [ngClass]="{ 'form-submit-attempt': formSubmitAttempted() }"
      autocomplete="off"
    >
      @if (disconnectReasons.length > 0) {
        <div class="mt-6">
          <label class="comcast-input-label" for="reason-trigger">
            Reason for disconnect
            <span class="comcast-required-marker" aria-hidden="true">*</span>
          </label>
          <div class="relative mt-2">
            <button
              id="reason-trigger"
              type="button"
              class="comcast-select-trigger"
              [class.comcast-select-trigger--open]="isReasonDropdownOpen()"
              [class.comcast-select-trigger--filled]="!!selectedReasonLabel"
              [class.comcast-input-invalid]="
                inlineErrors.reason &&
                (disconnectTxGroup.get('reason').dirty || formSubmitAttempted())
              "
              [attr.aria-expanded]="isReasonDropdownOpen()"
              aria-haspopup="listbox"
              aria-controls="reason-listbox"
              aria-required="true"
              [attr.aria-invalid]="
                inlineErrors.reason &&
                (disconnectTxGroup.get('reason').dirty || formSubmitAttempted())
              "
              [attr.aria-describedby]="
                inlineErrors.reason &&
                (disconnectTxGroup.get('reason').dirty || formSubmitAttempted())
                  ? 'reason-error'
                  : null
              "
              (click)="toggleReasonDropdown($event)"
            >
              <span [class.text-brand-muted]="!selectedReasonLabel">
                {{ selectedReasonLabel || 'Please select' }}
              </span>
              <mat-icon class="comcast-select-icon" aria-hidden="true">
                {{
                  isReasonDropdownOpen() ? 'arrow_drop_up' : 'arrow_drop_down'
                }}
              </mat-icon>
            </button>

            @if (isReasonDropdownOpen()) {
              <div
                id="reason-listbox"
                class="comcast-select-panel"
                role="listbox"
                aria-label="Reason for disconnect"
                [class.comcast-select-panel--open]="isReasonDropdownOpen()"
                [class.comcast-select-panel--invalid]="
                  inlineErrors.reason &&
                  (disconnectTxGroup.get('reason').dirty || formSubmitAttempted())
                "
                (click)="$event.stopPropagation()"
              >
                <div class="comcast-select-options">
                  @for (reason of disconnectReasons; track reason) {
                    <div
                      class="comcast-select-option"
                      role="option"
                      tabindex="0"
                      [attr.aria-selected]="disconnectTxGroup.value.reason === reason"
                      [class.comcast-select-option--selected]="
                        disconnectTxGroup.value.reason === reason
                      "
                      (click)="selectReason(reason, $event)"
                      (keydown.enter)="selectReason(reason, $event)"
                      (keydown.space)="selectReason(reason, $event)"
                    >
                      {{ reason }}
                    </div>
                  }
                </div>
              </div>
            }
          </div>
          @if (
            inlineErrors.reason &&
            (disconnectTxGroup.get('reason').dirty || formSubmitAttempted())
          ) {
            <div id="reason-error" class="comcast-validation-error" role="alert">
              Please select a reason for disconnect
            </div>
          }
        </div>

        @if (disconnectTxGroup.value.reason === 'Other') {
          <div class="mt-4">
            <label class="comcast-input-label" for="other-reason-input">Enter reason</label>
            <input
              id="other-reason-input"
              type="text"
              formControlName="otherReasonDetails"
              class="comcast-input mt-2"
              placeholder="Please describe your reason..."
              (keyup)="canSubmit()"
            />
          </div>
        }
      }

      <app-primary-reporter
        [formRef]="disconnectTxGroup"
        [reporters]="primaryReporters"
        [altContact]="false"
        (usersLoaded)="usersLoaded()"
      >
      </app-primary-reporter>

      <app-select-multiple-services
        [mode]="ServiceSelectMode.Disconnect"
        [formRef]="disconnectTxGroup"
        [inlineErrors]="inlineErrors"
        [selectedServiceId]="selectedServiceId"
        (servicesLoaded)="servicesLoaded()"
        (keyup)="canSubmit()"
      >
      </app-select-multiple-services>

      <div class="mt-6">
        <label class="comcast-input-label" for="disconnect-comments">Additional comments</label>
        <textarea
          id="disconnect-comments"
          formControlName="comments"
          class="comcast-textarea mt-2"
          rows="5"
          maxlength="400"
          placeholder="Add any details to help us process your request..."
          aria-describedby="disconnect-comments-count"
          (keyup)="canSubmit()"
        ></textarea>
        <div id="disconnect-comments-count" class="comcast-textarea-count" aria-live="polite" aria-atomic="true">
          {{ disconnectTxGroup.get('comments').value?.length || 0 }} / 400
        </div>
      </div>

      @if (submissionError) {
        <div class="comcast-error-alert mt-4" role="alert" aria-live="assertive">
          <mat-icon
            fontSet="material-icons-outlined"
            class="comcast-error-alert-icon"
            aria-hidden="true"
            >warning</mat-icon
          >
          <span class="comcast-error-alert-text">
            {{
              errorMessage || 'Unable to submit the ticket. Please try again!'
            }}
          </span>
        </div>
      }

      <div class="mt-6">
        <button
          class="btn-primary w-full"
          (click)="submitTicket()"
          [disabled]="areServicesLoading() || areUsersLoading() || isSubmitting"
          [attr.aria-busy]="isSubmitting"
          [attr.aria-label]="isSubmitting ? 'Submitting ticket, please wait' : 'Submit ticket'"
        >
          @if (isSubmitting) {
            <mat-spinner [diameter]="20" color="accent" aria-hidden="true"></mat-spinner>
          } @else {
            Submit ticket
          }
        </button>
      </div>
    </form>
  }
</section>
